import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import MFAVerify from './MFAVerify';
import './Login.css';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, completeMFALogin, mfaPending, clearMFAPending } = useAuth();
  const { branding } = useSettings();
  const { effectiveTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      if (!result.mfaRequired) {
        navigate(from, { replace: true });
      }
      // If MFA required, the MFAVerify modal will show
    } catch (error: any) {
      setError(error.response?.data?.error || t('auth.loginFailed', 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerify = async (code: string, isRecoveryCode: boolean, trustDevice: boolean, deviceName?: string) => {
    if (!mfaPending) return;

    setLoading(true);
    setError('');

    try {
      await completeMFALogin(mfaPending.mfaToken, code, isRecoveryCode, trustDevice, deviceName);
      navigate(from, { replace: true });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || t('auth.mfaVerificationFailed', 'Invalid verification code'));
    } finally {
      setLoading(false);
    }
  };

  const handleMFACancel = () => {
    clearMFAPending();
    setFormData({ email: '', password: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          {(branding.applicationLogo || branding.applicationLogoDark) && (
            <img
              src={effectiveTheme === 'dark' && branding.applicationLogoDark ? branding.applicationLogoDark : branding.applicationLogo}
              alt={branding.applicationName}
              className="login-logo"
            />
          )}
          <p className="login-subtitle">{t('auth.signInToContinue', 'Sign in to continue to')} {branding.applicationName}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('common.email', 'Email')}</label>
            <div className="input-group">
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth.emailPlaceholder', 'Enter your email')}
                required
              />
              <i className="fas fa-envelope input-icon"></i>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('common.password', 'Password')}</label>
            <div className="input-group">
              <input
                type="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.passwordPlaceholder', 'Enter your password')}
                required
              />
              <i className="fas fa-lock input-icon"></i>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {t('auth.signingIn', 'Signing In...')}
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt"></i>
                {t('auth.signIn', 'Sign In')}
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          <Link to="/forgot-password" style={{ color: 'var(--color-text-link)', textDecoration: 'none' }}>
            {t('auth.forgotPassword', 'Forgot Password?')}
          </Link>
        </div>

        <div className="login-register">
          <span>{t('login.noAccount', "Don't have an account?")}</span>
          {' '}
          <Link to="/pricing" className="login-register-link">
            {t('viewPlans', 'View Plans')}
          </Link>
          <span className="login-register-separator">|</span>
          <Link to="/register" className="login-register-link">
            {t('login.register', 'Register')}
          </Link>
        </div>
      </div>

      {/* MFA Verification Modal */}
      {mfaPending && (
        <MFAVerify
          email={mfaPending.email}
          onVerify={handleMFAVerify}
          onCancel={handleMFACancel}
        />
      )}
    </div>
  );
};

export default Login;
